import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "ui/Button";
import ServerGroupForm, {
  emptyTagRule,
  MatchRule,
  ruleRequiresKey,
  toMatchRuleInput,
} from "features/Server/ServerGroupForm";
import PreviewDiff from "features/Server/ServerGroupPreviewDiff";
import { useSetFlash, useSetNextFlashLocation } from "providers/WithFlashes";
import { WithCurrentOrganizationQuery } from "providers/WithCurrentOrganization/gql/Query.generated";
import { useRoutes } from "utils/routes";

import { useDeleteServerGroupMutation } from "features/Server/ServerGroupEdit/gql/Mutation.delete.generated";
import { useUpdateCustomServerGroupMutation } from "features/Server/ServerGroupEdit/gql/Mutation.update.generated";

type OrganizationType =
  WithCurrentOrganizationQuery["getCurrentUserDetails"]["organizations"][number];

type Props = {
  serverGroup: OrganizationType["customServerGroups"][number];
  currentOrganization: OrganizationType;
};

const ServerGroupEdit = ({ serverGroup, currentOrganization }: Props) => {
  const navigate = useNavigate();
  const { organizationServerGroupsSettings } = useRoutes();
  const [name, setName] = useState(serverGroup.name);
  const [description, setDescription] = useState(serverGroup.description ?? "");
  const [matchRules, setMatchRules] = useState<MatchRule[]>(() =>
    serverGroup.matchRules.length > 0
      ? serverGroup.matchRules.map((rule) => ({
          type: rule.matchType,
          key: rule.matchKey ?? "",
          value: rule.pattern,
        }))
      : [emptyTagRule()],
  );

  const setFlash = useSetFlash();
  const setNextFlashLocation = useSetNextFlashLocation();
  const [updateServerGroup, { loading: updating }] =
    useUpdateCustomServerGroupMutation();
  const [deleteServerGroup, { loading: deleting }] =
    useDeleteServerGroupMutation();

  const disabled = !serverGroup.permittedToEdit;
  const loading = updating || deleting;
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const saveDisabled = disabled || loading;

  const handleSave = () => {
    if (trimmedName === "") {
      setFlash("error", "Name is required.");
      return;
    }
    if (
      matchRules.some(
        (rule) =>
          (ruleRequiresKey(rule) && rule.key.trim() === "") ||
          rule.value.trim() === "",
      )
    ) {
      setFlash("error", "All match rule fields are required.");
      return;
    }
    updateServerGroup({
      variables: {
        serverGroupId: serverGroup.id,
        name: trimmedName,
        description: trimmedDescription === "" ? null : trimmedDescription,
        matchRules: toMatchRuleInput(matchRules),
      },
      refetchQueries: ["ServerGroupMembers"],
      awaitRefetchQueries: true,
      onError: (error) => {
        setFlash("error", `Failed to update server group: ${error.message}`);
      },
      onCompleted: (data) => {
        const errors = data.updateServerGroup.errors;
        if (errors && errors.length > 0) {
          setFlash("error", errors.join(", "));
          return;
        }
        setFlash("success", "Server group updated");
      },
    });
  };

  const handleCancel = () => {
    navigate(organizationServerGroupsSettings(currentOrganization.slug));
  };

  const handleDelete = () => {
    if (!window.confirm(`Delete server group "${serverGroup.name}"?`)) {
      return;
    }
    deleteServerGroup({
      variables: { serverGroupId: serverGroup.id },
      refetchQueries: ["WithCurrentOrganization"],
      awaitRefetchQueries: true,
      onError: (error) => {
        setFlash("error", `Failed to delete server group: ${error.message}`);
      },
      onCompleted: () => {
        setFlash("success", "Server group deleted");
        setNextFlashLocation(
          organizationServerGroupsSettings(currentOrganization.slug),
        );
        navigate(organizationServerGroupsSettings(currentOrganization.slug));
      },
    });
  };

  return (
    <div className="flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <ServerGroupForm
          name={name}
          description={description}
          matchRules={matchRules}
          disabled={disabled}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onMatchRulesChange={setMatchRules}
        />
        <div className="flex items-center gap-2">
          <Button
            bare
            onClick={handleSave}
            className="btn btn-success"
            disabled={saveDisabled}
          >
            Save
          </Button>
          <Button bare onClick={handleCancel} variant="link">
            Cancel
          </Button>
          {serverGroup.permittedToDelete ? (
            <Button
              bare
              onClick={handleDelete}
              className="btn btn-danger ml-auto"
              disabled={loading}
            >
              Delete
            </Button>
          ) : null}
        </div>
      </div>
      <div className="w-[390px] shrink-0">
        <PreviewDiff
          organizationSlug={currentOrganization.slug}
          serverGroupId={serverGroup.id}
          matchRules={matchRules}
        />
      </div>
    </div>
  );
};

export default ServerGroupEdit;
